import { randomString } from './helpers';

/**
 * สร้าง Unique Key สำหรับ Variant เพื่อใช้เปรียบเทียบ
 * เช่น: "1:1|4:15"
 */
function generateVariantKey(attributes) {
  if (!attributes || attributes.length === 0) return "";
  return attributes
    .map(a => ({ id: parseInt(a.id), option_id: parseInt(a.option_id) }))
    .sort((a, b) => a.id - b.id)
    .map(a => `${a.id}:${a.option_id}`)
    .join('|');
}

/**
 * ฟังก์ชันหลักสำหรับ Sync Variants
 * @param {Array} currentVariants - Variants ปัจจุบัน
 * @param {Array} attributesConfig - Config ของ Attributes/Options
 * @param {String} baseName - ชื่อสินค้าหลัก (เช่น "ก๋วยเตี๋ยวเรือ")
 * @param {String} baseCode - รหัสสินค้าหลัก (เช่น "S001")
 */
export function syncVariants(currentVariants, attributesConfig, baseName = '', baseCode = '', baseServingSizeUnitId = '', baseUnitId = '', baseNameEn = '') {
  // 1. แปลงโครงสร้างให้เป็น Array ของตัวเลือก
  // *** สำคัญ: ต้อง map เอา option_name และ option_code มาด้วย เพื่อใช้ Gen ชื่อ ***
  const attrsWithOptions = attributesConfig.map(attr => {
    return attr.options.map(opt => ({
      id: parseInt(attr.id),
      option_id: parseInt(opt.attribute_option_id),
      option_name: opt.name || '',
      option_name_en: opt.name_en || '',
      option_code: opt.code || ''
    }));
  });

  const attributeOrderMap = new Map();
  attributesConfig.forEach((attr, index) => {
    attributeOrderMap.set(parseInt(attr.id), index);
  });

    if (attrsWithOptions.length === 0) return [];

    // 2. Cartesian Product: จับคู่ทุกความเป็นไปได้
  const cartesian = (arrays) => {
    return arrays.reduce((acc, curr) => {
      return acc.flatMap(c => curr.map(n => [].concat(c, n)));
    }, [[]]);
  };

  const allCombinations = cartesian(attrsWithOptions);

  // 3. Map ของเดิมไว้ เพื่อรักษาข้อมูล (Price, SKU, ID) ที่ user เคยกรอก
  const existingMap = new Map();
  currentVariants.forEach(variant => {
    if (variant.attributes) {
      const key = generateVariantKey(variant.attributes);
      existingMap.set(key, variant);
    }
  });

  // 4. สร้าง List ใหม่
  const newVariants = allCombinations
    .filter(combo => combo.length > 0)
    .map(combo => {
      const key = generateVariantKey(combo);

      if (existingMap.has(key)) {
        // CASE A: มีอยู่แล้ว -> ใช้ตัวเดิม (ข้อมูลไม่หาย)
        return existingMap.get(key);
      } else {
        // CASE B: สร้างตัวใหม่ พร้อม Gen Name/Code

        // เรียงลำดับ Attribute เพื่อให้การ gen ชื่อเรียงสวยงามเสมอ (เช่น ไซส์ มาก่อน รสชาติ)
        const sortedCombo = [...combo].sort((a, b) => {
          const indexA = attributeOrderMap.get(parseInt(a.id)) ?? 999; // ถ้าหาไม่เจอให้ไปอยู่ท้ายสุด
          const indexB = attributeOrderMap.get(parseInt(b.id)) ?? 999;
          return indexA - indexB; // เรียงจากน้อยไปมาก (ตามลำดับ array)
        });

        // Gen Name Logic: "ชื่อหลัก" + " " + "ชื่อตัวเลือก"
        const optionNames = sortedCombo.map(c => c.option_name).filter(n => n).join(' ');
        const genName = baseName ? `${baseName} ${optionNames}`.trim() : optionNames;

        // Gen ชื่ออังกฤษ: ทำให้เฉพาะเมื่อ "ชื่อสินค้าหลัก EN" และ "ชื่อตัวเลือก EN" ครบทุกตัว
        // ขาดชิ้นใดชิ้นหนึ่ง = ปล่อยว่าง ดีกว่าได้ชื่อครึ่งไทยครึ่งอังกฤษ (admin เติมเองทีหลังได้)
        const optionNamesEn = sortedCombo.map(c => c.option_name_en);
        const genNameEn = (baseNameEn && optionNamesEn.every(n => n))
          ? `${baseNameEn} ${optionNamesEn.join(' ')}`.trim()
          : '';

        // Gen Code Logic: "รหัสหลัก" + "-" + "รหัสตัวเลือก"
        // (ถ้าไม่มี option_code ให้ใช้ option_name แทน)
        const optionCodes = sortedCombo.map(c => c.option_code || c.option_name).filter(c => c).join('-');
        const genCode = baseCode ? `${baseCode}-${optionCodes}` : optionCodes;

        return {
          code: genCode,
          barcode: randomString(10),
          name: genName,
          name_en: genNameEn, // ชื่ออังกฤษ optional — ว่างได้ ระบบจะ fallback ไปใช้ชื่อไทย
          description: '',
          description_en: '',
          price: '', // ให้ User ไปกรอกเอง หรือรับ Default มาก็ได้
          compare_at_price: '', // ราคาเต็ม (ขีดฆ่า) — optional ต่อ variant
          dine_in_only: false, // ทานที่ร้านเท่านั้น — ตั้งรายตัวได้
          is_active: true,
          serving_size_value: '',
          serving_size_unit_id: baseServingSizeUnitId,
          unit_id: baseUnitId,
          // เก็บ attributes พร้อม metadata (name/code) ไว้ใช้รอบหน้า <selection-tag id="1">
          attributes: sortedCombo.map(c => ({
            id: c.id,
            option_id: c.option_id,
            option_name: c.option_name, // เพิ่มบรรทัดนี้: เก็บชื่อตัวเลือกไว้ใช้รอบหน้า
            option_name_en: c.option_name_en, // เก็บชื่ออังกฤษของตัวเลือกไว้ใช้รอบหน้า
            option_code: c.option_code  // เพิ่มบรรทัดนี้: เก็บรหัสตัวเลือกไว้ใช้รอบหน้า
          }))
        };
      }
    });

  return newVariants;
}
